import React, { useState, useEffect, useCallback } from 'react';
import { parcelsApi, farmsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import LocationPickerWithBoundary from '../components/map/LocationPickerWithBoundary';
import { Grid3X3, Plus, MoreHorizontal, Pencil, Trash2, Loader2, Ruler, Leaf, Mountain, Map, MapPin } from 'lucide-react';

const ParcelsPage = () => {
  const [parcels, setParcels] = useState([]);
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingParcel, setEditingParcel] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    area: '',
    crop_type: '',
    soil_type: '',
    farm_id: '',
    latitude: '',
    longitude: '',
    boundary: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const { isAdmin } = useAuth();

  const handleLocationChange = useCallback((lat, lng) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6),
    }));
  }, []);

  const handleBoundaryChange = useCallback((boundary) => {
    setFormData(prev => ({
      ...prev,
      boundary: boundary,
    }));
  }, []);

  const handleAreaCalculated = useCallback((area) => {
    setFormData(prev => ({
      ...prev,
      area: area.toFixed(2),
    }));
  }, []);

  const fetchData = async () => {
    try {
      const [parcelsRes, farmsRes] = await Promise.all([
        parcelsApi.getAll(),
        farmsApi.getAll(),
      ]);
      setParcels(parcelsRes.data);
      setFarms(farmsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (parcel = null) => {
    if (parcel) {
      setEditingParcel(parcel);
      setFormData({
        name: parcel.name,
        description: parcel.description || '',
        area: parcel.area?.toString() || '',
        crop_type: parcel.crop_type || '',
        soil_type: parcel.soil_type || '',
        farm_id: parcel.farm_id,
        latitude: parcel.latitude?.toString() || '',
        longitude: parcel.longitude?.toString() || '',
        boundary: parcel.boundary || [],
      });
    } else {
      setEditingParcel(null);
      setFormData({
        name: '',
        description: '',
        area: '',
        crop_type: '',
        soil_type: '',
        farm_id: farms[0]?.id || '',
        latitude: '',
        longitude: '',
        boundary: [],
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const data = {
      name: formData.name,
      description: formData.description || null,
      area: formData.area ? parseFloat(formData.area) : null,
      crop_type: formData.crop_type || null,
      soil_type: formData.soil_type || null,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      boundary: formData.boundary?.length >= 3 ? formData.boundary : null,
      farm_id: formData.farm_id,
    };

    try {
      if (editingParcel) {
        await parcelsApi.update(editingParcel.id, {
          name: data.name,
          description: data.description,
          area: data.area,
          crop_type: data.crop_type,
          soil_type: data.soil_type,
          latitude: data.latitude,
          longitude: data.longitude,
          boundary: data.boundary,
        });
        toast.success('Parcelle mise à jour');
      } else {
        await parcelsApi.create(data);
        toast.success('Parcelle créée');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette parcelle ?')) return;

    try {
      await parcelsApi.delete(id);
      toast.success('Parcelle supprimée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const getFarmName = (farmId) => {
    const farm = farms.find((f) => f.id === farmId);
    return farm?.name || 'Inconnu';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parcels-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Parcelles</h1>
          <p className="text-muted-foreground mt-1">Gérez vos parcelles agricoles</p>
        </div>
        {isAdmin && farms.length > 0 && (
          <Button onClick={() => handleOpenDialog()} data-testid="add-parcel-btn">
            <Plus className="mr-2 h-4 w-4" />
            Nouvelle parcelle
          </Button>
        )}
      </div>

      {/* Cards Grid */}
      {parcels.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Grid3X3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Aucune parcelle</p>
            {farms.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                Créez d'abord une exploitation
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {parcels.map((parcel) => (
            <Card key={parcel.id} className="card-hover" data-testid={`parcel-card-${parcel.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary/10">
                      <Grid3X3 className="h-5 w-5 text-secondary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{parcel.name}</CardTitle>
                      <CardDescription className="text-sm mt-0.5">
                        {getFarmName(parcel.farm_id)}
                      </CardDescription>
                    </div>
                  </div>
                  {isAdmin && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`parcel-menu-${parcel.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenDialog(parcel)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Modifier
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => handleDelete(parcel.id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {parcel.description && (
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {parcel.description}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  {parcel.area && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Ruler className="h-3 w-3" />
                      {parcel.area} ha
                    </Badge>
                  )}
                  {parcel.crop_type && (
                    <Badge variant="outline" className="flex items-center gap-1 bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/20 dark:text-emerald-400 dark:border-emerald-800">
                      <Leaf className="h-3 w-3" />
                      {parcel.crop_type}
                    </Badge>
                  )}
                  {parcel.soil_type && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Mountain className="h-3 w-3" />
                      {parcel.soil_type}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[90vw] max-w-[90vw] h-[85vh] max-h-[85vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-xl">
              {editingParcel ? 'Modifier la parcelle' : 'Nouvelle parcelle'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto pr-2">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4">
                {/* Left column - Form fields */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nom de la parcelle *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Parcelle Nord"
                        required
                        data-testid="parcel-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="area">Surface (hectares)</Label>
                      <Input
                        id="area"
                        type="number"
                        step="0.01"
                        value={formData.area}
                        onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                        placeholder="Calculée automatiquement"
                        data-testid="parcel-area-input"
                      />
                      {formData.boundary?.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          Surface calculée depuis le tracé
                        </p>
                      )}
                    </div>
                  </div>

                  {!editingParcel && (
                    <div className="space-y-2">
                      <Label htmlFor="farm_id">Exploitation *</Label>
                      <Select
                        value={formData.farm_id}
                        onValueChange={(value) => setFormData({ ...formData, farm_id: value })}
                      >
                        <SelectTrigger data-testid="parcel-farm-select">
                          <SelectValue placeholder="Sélectionner une exploitation" />
                        </SelectTrigger>
                        <SelectContent>
                          {farms.map((farm) => (
                            <SelectItem key={farm.id} value={farm.id}>
                              {farm.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="crop_type">Type de culture</Label>
                      <Input
                        id="crop_type"
                        value={formData.crop_type}
                        onChange={(e) => setFormData({ ...formData, crop_type: e.target.value })}
                        placeholder="Blé, Vigne, Maïs..."
                        data-testid="parcel-crop-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="soil_type">Type de sol</Label>
                      <Input
                        id="soil_type"
                        value={formData.soil_type}
                        onChange={(e) => setFormData({ ...formData, soil_type: e.target.value })}
                        placeholder="Argilo-calcaire..."
                        data-testid="parcel-soil-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Description de la parcelle, historique des cultures..."
                      rows={3}
                      data-testid="parcel-description-input"
                    />
                  </div>

                  {/* Boundary info */}
                  {formData.boundary?.length > 0 && (
                    <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 p-3">
                      <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
                        <MapPin className="h-4 w-4" />
                        <span className="font-medium">Limites définies</span>
                      </div>
                      <p className="text-xs text-emerald-600 dark:text-emerald-500 mt-1">
                        Périmètre tracé avec {formData.boundary.length} points
                      </p>
                    </div>
                  )}
                </div>

                {/* Right column - Map */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Map className="h-4 w-4" />
                    Emplacement et limites sur la carte
                  </Label>
                  <LocationPickerWithBoundary
                    latitude={formData.latitude ? parseFloat(formData.latitude) : null}
                    longitude={formData.longitude ? parseFloat(formData.longitude) : null}
                    boundary={formData.boundary}
                    onLocationChange={handleLocationChange}
                    onBoundaryChange={handleBoundaryChange}
                    onAreaCalculated={handleAreaCalculated}
                    height="calc(85vh - 280px)"
                  />
                </div>
              </div>
            </div>
            
            <DialogFooter className="shrink-0 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={submitting} data-testid="parcel-submit-btn">
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingParcel ? 'Mettre à jour' : 'Créer la parcelle'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParcelsPage;
